/**
 * Usage Pattern 推斷（v0.3 重構消耗模型）
 *
 * 物品分兩類，UI 操作完全不同：
 *
 * - **unit_per_use**（一次一單位）— 例：蛋、罐裝飲料、零食包、小瓶牛奶
 *   操作：直接扣 1 顆 / 1 包 / 1 罐；扣完 finalize
 *
 * - **continuous**（持續使用）— 例：醬油、沙拉油、大瓶飲料、5kg 米
 *   操作：未開封 → 開瓶 → 使用中 → 用完 / 過期 / 丟棄
 *   不追蹤分次扣量（家庭場景沒人會記每次用 1ml）
 *
 * 推斷依 category 為主，compareBaseUnit + packageSize 為輔。
 */

import type { Category } from '../events/types.js';

export type UsagePattern = 'unit_per_use' | 'continuous';

export interface ClassifyInput {
  category?: Category;
  compareBaseUnit?: string;
  packageSize?: number;
}

const PER_100_UNITS = new Set(['ml', 'g', '公克', '毫升']);

/**
 * 規則（從強到弱）：
 * 1. condiment（醬油/醋/油/辛香料/糖/鹽）→ 永遠 continuous
 * 2. beverage 飲料 → 看容量：≥ 500ml continuous（大瓶持續喝）；< 500ml unit（罐裝）
 * 3. household（洗劑/牙膏/洗髮精）→ ml/g 容量類 continuous；其他（衛生紙抽/濕紙巾）unit
 * 4. dry_good 米麵粉糖鹽 → ≥ 500g continuous；其他（餅乾/泡麵）unit
 * 5. dairy_egg → ≥ 500ml continuous（大瓶鮮乳）；其他 unit（蛋/起司片/小瓶）
 * 6. frozen / vegetable / fruit / meat / seafood / other → unit_per_use 預設
 */
export function classifyUsagePattern(input: ClassifyInput): UsagePattern {
  const { category, compareBaseUnit, packageSize } = input;
  const unit = compareBaseUnit ?? '';
  const size = packageSize ?? 0;
  const isCapacity = PER_100_UNITS.has(unit);

  if (category === 'condiment') return 'continuous';

  if (category === 'beverage') {
    return isCapacity && size >= 500 ? 'continuous' : 'unit_per_use';
  }

  if (category === 'household') {
    return isCapacity ? 'continuous' : 'unit_per_use';
  }

  if (category === 'dry_good') {
    return isCapacity && size >= 500 ? 'continuous' : 'unit_per_use';
  }

  if (category === 'dairy_egg') {
    return isCapacity && size >= 500 ? 'continuous' : 'unit_per_use';
  }

  // frozen / vegetable / fruit / meat / seafood / other
  return 'unit_per_use';
}
